import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { oneSignalService } from '@/lib/onesignal';
import { isNativeApp } from '@/lib/platform';

/**
 * OneSignalProvider handles OneSignal re-linking when the user signs in.
 * 
 * NOTE: This provider does NOT request notification permissions automatically.
 * Permissions should be requested via the NotificationPromptDialog flow which is
 * triggered by:
 * - First visit to social tab (after 2s delay)
 * - Third app visit (after 2s delay)
 * - Manual enable in Settings
 * 
 * This provider only handles:
 * - Re-initializing OneSignal on sign-in
 * - Re-linking external ID if user already has permissions
 * - Saving player ID if permissions were granted elsewhere
 * 
 * This enables ALL notification types once onesignal_player_id is set:
 * - Follow request notifications (automatic)
 * - Penalty charge notifications (automatic)
 * - Graph creation notifications (automatic)
 * - Reaction notifications (automatic)
 * - Goal reminder notifications (controlled by toggle in settings)
 * 
 * When a user signs out, the external ID link is removed and player ID is cleared.
 */
export const OneSignalProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    // Only run in native app
    if (!isNativeApp()) {
      console.log('[OneSignalProvider] Skipping - not in native app');
      return;
    }

    const initializeOneSignal = async (userId: string) => {
      try {
        // Check if user already has a player ID in the database
        // If they do, we can safely re-link the external ID
        const { data: profile } = await supabase
          .from('profiles')
          .select('onesignal_player_id')
          .eq('id', userId)
          .single();

        if (profile?.onesignal_player_id) {
          console.log('[OneSignalProvider] User already has player ID, re-linking external ID...');
          
          // Re-initialize OneSignal if needed
          const initialized = await oneSignalService.initialize();
          if (initialized) {
            // Re-link external ID in case user signed out and back in
            await oneSignalService.setExternalId(userId);
            console.log('[OneSignalProvider] External ID re-linked');
          }
          return;
        }

        // If user doesn't have a player ID, they haven't gone through the
        // NotificationPromptDialog flow yet. Don't do anything - just wait.
        console.log('[OneSignalProvider] No player ID found - user needs to grant permission via app flow first');
        
      } catch (error) {
        console.error('[OneSignalProvider] Error during initialization:', error);
      }
    };

    // Listen to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        console.log('[OneSignalProvider] User signed in, checking for existing player ID...');
        // Delay to ensure app is fully loaded
        setTimeout(() => initializeOneSignal(session.user.id), 2000);
      } else if (event === 'SIGNED_OUT') {
        console.log('[OneSignalProvider] User signed out, OneSignal cleanup already handled by auth.ts');
      }
    });

    // Also check current session on mount
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        console.log('[OneSignalProvider] Current user found on mount, checking for existing player ID...');
        setTimeout(() => initializeOneSignal(user.id), 2000);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return <>{children}</>;
};
